import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { AlertTriangle, CheckCircle, Clock, Loader2, RefreshCw, ShieldCheck, ShieldX, Video } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from './index';
import {
  AfwijsReden,
  ComplianceStatus,
  DUUR_OPTIES,
  intervisieApi,
  IntervisieGroupMember,
  IntervisieSession,
  METHODIEKEN,
  TeamsSyncResultaat,
} from '../services/intervisieApi';

const uren = (min: number) => (min / 60).toLocaleString('nl-NL', { maximumFractionDigits: 1 });
const pctTekst = (v: number | string | null | undefined) => Number(v ?? 0).toLocaleString('nl-NL', { maximumFractionDigits: 1 });
const foutTekst = (err: any, standaard: string) => err?.response?.data?.message || standaard;
const DREMPEL_PCT = 90;
const DEELNEMEN_VOORAF_MIN = 15;

/** Groen/rood label met het gemeten aanwezigheidspercentage. */
const ComplianceChip: React.FC<{ status: ComplianceStatus; pct: number | string | null; reden: AfwijsReden | null }> = ({ status, pct, reden }) => {
  const { t } = useTranslation('pages');
  const ok = status === 'ATTENDANCE_VERIFIED';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold ${ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}
      title={ok ? undefined : reden === 'NIET_VIA_COCKPIT' ? t('intervisie.teams.reasonCockpit') : t('intervisie.teams.reasonThreshold', { min: DREMPEL_PCT })}
    >
      {ok ? <ShieldCheck className="w-3 h-3" /> : <ShieldX className="w-3 h-3" />}
      {ok
        ? t('intervisie.teams.verified', { pct: pctTekst(pct) })
        : reden === 'NIET_VIA_COCKPIT'
          ? t('intervisie.teams.rejectedCockpit')
          : t('intervisie.teams.rejected', { pct: pctTekst(pct) })}
    </span>
  );
};

/** Eén sessieregel: datum, duur, Teams-status en (voor de contactpersoon) de acties. */
export const IntervisieSessieRij: React.FC<{
  groupId: string;
  sessie: IntervisieSession;
  isContactPerson: boolean;
  members?: IntervisieGroupMember[];
}> = ({ groupId, sessie, isContactPerson, members = [] }) => {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const [bewerkDuur, setBewerkDuur] = useState(false);
  const [duur, setDuur] = useState(sessie.duur_minuten ?? 120);
  const ververs = () => queryClient.invalidateQueries({ queryKey: ['intervisie-group', groupId] });

  const opslaanDuur = useMutation({
    mutationFn: () => intervisieApi.updateSessionDuration(sessie.id, duur),
    onSuccess: () => {
      setBewerkDuur(false);
      ververs();
    },
    onError: (err) => toast.error(foutTekst(err, t('intervisie.error'))),
  });

  const meldResultaat = (r: TeamsSyncResultaat) => {
    toast.success(t('intervisie.teams.syncDone', { matched: r.gekoppeld, verified: r.geverifieerd, rejected: r.afgewezen }));
    if (r.onbekend.length) toast(t('intervisie.teams.syncGuests', { count: r.onbekend.length }), { duration: 8000 });
  };

  const sync = useMutation({
    mutationFn: () => intervisieApi.syncTeamsAttendance(sessie.id),
    onSuccess: (r) => {
      ververs();
      meldResultaat(r);
    },
    onError: (err) => {
      ververs();
      toast.error(foutTekst(err, t('intervisie.error')));
    },
  });

  const koppel = useMutation({
    mutationFn: (v: { user_id: string; sleutel: string }) => intervisieApi.linkTeamsParticipant(sessie.id, v.user_id, v.sleutel),
    onSuccess: () => {
      ververs();
      toast.success(t('intervisie.teams.linkDone'));
    },
    onError: (err) => toast.error(foutTekst(err, t('intervisie.error'))),
  });

  // De Grendel: de Teams-link komt pas na de gelogde klik. Het venster wordt vóór de
  // API-aanroep geopend, anders houdt de pop-upblokkering het tegen.
  const deelnemen = useMutation({
    mutationFn: async () => {
      const venster = window.open('', '_blank');
      try {
        const { join_url } = await intervisieApi.joinTeamsSession(sessie.id);
        if (venster) {
          venster.opener = null;
          venster.location.href = join_url;
        } else {
          window.location.href = join_url;
        }
      } catch (e) {
        venster?.close();
        throw e;
      }
    },
    onError: (err) => toast.error(foutTekst(err, t('intervisie.error'))),
  });

  const start = sessie.start_at ? new Date(sessie.start_at) : null;
  const eind = start ? new Date(start.getTime() + (sessie.duur_minuten ?? 120) * 60_000) : null;
  const nu = Date.now();
  const deelnameOpen = start ? start.getTime() - DEELNEMEN_VOORAF_MIN * 60_000 : 0;
  const toonJoin = !!sessie.kan_deelnemen && !!eind && nu < eind.getTime();
  const isAfgelopen = !!eind && nu >= eind.getTime();
  const attendees = sessie.attendees ?? [];
  const teamsRegels = new Map(attendees.filter((a) => a.bron === 'teams').map((a) => [a.user_id, a]));
  const onbekend = sessie.teams_onbekende_deelnemers ?? [];

  return (
    <div className="p-3 bg-gray-50 rounded-lg">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="text-sm font-medium text-gray-800">
            {start ? format(start, "d MMMM yyyy 'om' HH:mm", { locale: nl }) : format(new Date(sessie.date), 'd MMMM yyyy', { locale: nl })}
          </span>
          {sessie.methodology && <span className="ml-2 text-xs text-gray-500">· {sessie.methodology}</span>}
          <span className="ml-2 inline-flex items-center gap-1 text-xs text-gray-500">
            <Clock className="w-3 h-3" /> {t('intervisie.durationOption', { hours: uren(sessie.duur_minuten ?? 120) })}
          </span>
          {sessie.is_teams && (
            <span className="ml-2 inline-flex items-center gap-1 rounded bg-indigo-50 px-1.5 py-0.5 text-[11px] font-semibold text-indigo-700">
              <Video className="w-3 h-3" /> Teams
            </span>
          )}
          {sessie.mijn_aanwezigheid && (
            <span className="ml-2">
              <ComplianceChip status={sessie.mijn_aanwezigheid.status} pct={sessie.mijn_aanwezigheid.pct} reden={sessie.mijn_aanwezigheid.reden} />
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {toonJoin &&
            (nu >= deelnameOpen ? (
              <button
                type="button"
                disabled={deelnemen.isPending}
                onClick={() => deelnemen.mutate()}
                title={t('intervisie.teams.joinHint')}
                className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
              >
                {deelnemen.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Video className="w-3.5 h-3.5" />} {t('intervisie.teams.join')}
              </button>
            ) : (
              <span className="text-xs text-gray-500">{t('intervisie.teams.joinFrom', { time: format(new Date(deelnameOpen), 'HH:mm') })}</span>
            ))}
          {isContactPerson && sessie.is_teams && isAfgelopen && (
            <button
              type="button"
              disabled={sync.isPending}
              onClick={() => sync.mutate()}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#0B1F3A] hover:underline disabled:opacity-50"
            >
              {sync.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {t('intervisie.teams.syncNow')}
            </button>
          )}
          {isContactPerson && !sessie.is_teams && !bewerkDuur && (
            <button type="button" onClick={() => setBewerkDuur(true)} className="text-xs font-semibold text-[#0B1F3A] hover:underline">
              {t('intervisie.editDuration')}
            </button>
          )}
        </div>
      </div>

      {bewerkDuur && (
        <div className="mt-2 flex items-center gap-2">
          <select value={duur} onChange={(e) => setDuur(Number(e.target.value))} className="text-sm border border-gray-300 rounded px-2 py-1">
            {[...new Set([...DUUR_OPTIES, sessie.duur_minuten ?? 120])].sort((a, b) => a - b).map((m) => (
              <option key={m} value={m}>{t('intervisie.durationOption', { hours: uren(m) })}</option>
            ))}
          </select>
          <Button size="sm" loading={opslaanDuur.isPending} onClick={() => opslaanDuur.mutate()}>{t('intervisie.save')}</Button>
          <button type="button" onClick={() => setBewerkDuur(false)} className="text-xs text-gray-500 hover:underline">{t('intervisie.cancel')}</button>
        </div>
      )}

      {sessie.mijn_aanwezigheid?.status === 'REJECTED' && (
        <p className="mt-1 text-xs text-red-700">
          {sessie.mijn_aanwezigheid.reden === 'NIET_VIA_COCKPIT'
            ? t('intervisie.teams.reasonCockpit')
            : t('intervisie.teams.reasonThreshold', { min: DREMPEL_PCT })}
        </p>
      )}

      {sessie.is_teams && (
        <p className="mt-1 text-xs text-gray-500">
          {sessie.teams_sync_status === 'gesynchroniseerd' ? (
            <span className="inline-flex items-center gap-1 text-green-700">
              <CheckCircle className="w-3 h-3" />
              {t('intervisie.teams.statusSynced', { count: sessie.aanwezig_teams ?? 0 })}
            </span>
          ) : sessie.teams_sync_status === 'fout' ? (
            <span className="inline-flex items-center gap-1 text-red-600">
              <AlertTriangle className="w-3 h-3" />
              {isContactPerson && sessie.teams_sync_fout ? sessie.teams_sync_fout : t('intervisie.teams.statusError')}
            </span>
          ) : (
            t('intervisie.teams.statusPlanned', { pct: DREMPEL_PCT })
          )}
        </p>
      )}

      {isContactPerson && sessie.is_teams && sessie.teams_sync_status === 'gesynchroniseerd' && (
        <div className="mt-2 space-y-2">
          <ul className="divide-y divide-gray-100 rounded border border-gray-200 bg-white">
            {members.map((m) => {
              const regel = teamsRegels.get(m.user_id);
              return (
                <li key={m.user_id} className="flex flex-wrap items-center justify-between gap-2 px-2 py-1 text-xs text-gray-700">
                  <span>
                    {m.first_name} {m.last_name}
                    {regel?.koppeling === 'contactpersoon' && <span className="ml-1 text-[11px] text-gray-400">({t('intervisie.teams.linkedByContact')})</span>}
                  </span>
                  {regel?.compliance_status ? (
                    <ComplianceChip status={regel.compliance_status} pct={regel.aanwezigheid_pct} reden={regel.afwijs_reden} />
                  ) : (
                    <span className="text-[11px] text-gray-400">{t('intervisie.teams.notInReport')}</span>
                  )}
                </li>
              );
            })}
          </ul>

          {onbekend.length > 0 && (
            <div className="rounded border border-amber-200 bg-amber-50 p-2">
              <p className="text-xs font-semibold text-amber-800">{t('intervisie.teams.unknownTitle')}</p>
              <p className="text-[11px] text-amber-700 mb-1">{t('intervisie.teams.unknownHint')}</p>
              {onbekend.map((o) => (
                <div key={o.sleutel} className="flex flex-wrap items-center justify-between gap-2 py-0.5 text-xs text-gray-700">
                  <span>
                    {o.naam} <span className="text-gray-400">· {o.minuten} min</span>
                  </span>
                  <select
                    defaultValue=""
                    disabled={koppel.isPending}
                    onChange={(e) => e.target.value && koppel.mutate({ user_id: e.target.value, sleutel: o.sleutel })}
                    className="text-xs border border-gray-300 rounded px-1.5 py-0.5"
                    aria-label={t('intervisie.teams.unknownSelect')}
                  >
                    <option value="">{t('intervisie.teams.unknownSelect')}</option>
                    {members
                      .filter((m) => !teamsRegels.has(m.user_id))
                      .map((m) => (
                        <option key={m.user_id} value={m.user_id}>
                          {m.first_name} {m.last_name}
                        </option>
                      ))}
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Contactpersoon: Teams-vergadering laten aanmaken door Belagom. */
export const IntervisieTeamsPlanKaart: React.FC<{ groupId: string; beschikbaar: boolean }> = ({ groupId, beschikbaar }) => {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const [start, setStart] = useState('');
  const [duur, setDuur] = useState(120);
  const [methodiek, setMethodiek] = useState('');

  const plan = useMutation({
    mutationFn: () => intervisieApi.planTeamsSession(groupId, { start_at: new Date(start).toISOString(), duur_minuten: duur, methodology: methodiek || undefined }),
    onSuccess: () => {
      setStart('');
      queryClient.invalidateQueries({ queryKey: ['intervisie-group', groupId] });
      toast.success(t('intervisie.teams.planned'));
    },
    onError: (err) => toast.error(foutTekst(err, t('intervisie.error'))),
  });

  if (!beschikbaar) {
    return <p className="mt-3 text-xs text-gray-400">{t('intervisie.teams.notConfigured')}</p>;
  }

  const minimaal = format(new Date(Date.now() + 5 * 60_000), "yyyy-MM-dd'T'HH:mm");

  return (
    <div className="mt-4 border-t border-gray-100 pt-4 space-y-2">
      <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
        <Video className="w-4 h-4 text-indigo-600" /> {t('intervisie.teams.planTitle')}
      </p>
      <p className="text-xs text-gray-500">{t('intervisie.teams.planHint')}</p>
      <input
        type="datetime-local"
        value={start}
        min={minimaal}
        onChange={(e) => setStart(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        aria-label={t('intervisie.teams.start')}
      />
      <select value={duur} onChange={(e) => setDuur(Number(e.target.value))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" aria-label={t('intervisie.sessionDuration')}>
        {DUUR_OPTIES.map((m) => (
          <option key={m} value={m}>{t('intervisie.durationOption', { hours: uren(m) })}</option>
        ))}
      </select>
      <select value={methodiek} onChange={(e) => setMethodiek(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" aria-label={t('intervisie.sessionMethodology')}>
        <option value="">{t('intervisie.selectMethodology')}</option>
        {METHODIEKEN.map((m) => (
          <option key={m} value={m}>{m}</option>
        ))}
      </select>
      <Button className="w-full" size="sm" variant="outline" disabled={!start} loading={plan.isPending} onClick={() => plan.mutate()}>
        <Video className="w-4 h-4 mr-2" /> {t('intervisie.teams.planButton')}
      </Button>
    </div>
  );
};
